import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import HeaderMenu from "../components/HeaderMenu";
import { CustomModal } from "../components/CustomModal";
import { Icon } from "@mui/material";
import { API_URL } from "../api";

export default function FormInspeksi() {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [userName, setUserName] = useState("");
    const [userJabatan, setUserJabatan] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Modal State
    const [modalVisible, setModalVisible] = useState(false);
    const [modalProps, setModalProps] = useState({
        title: "",
        message: "",
        type: "info",
        buttons: []
    });

    // Initialize form data
    const [formData, setFormData] = useState({
        tanggal: "",
        lokasi: "",
        inspectors: [{ nama: "", perusahaan: "", jabatan: "" }],
        answers: {},
        photos: {},
        keterangan: {}
    });

    // Questions grouping configuration
    const questionSections = [
        {
            title: "Umum",
            questions: [
                "Pengendalian debu: debu terkendali, terdapat jadwal penyiraman dan jumlah water truck mencukupi.",
                "Terdapat rambu lalu lintas tambang yang memadai: penunjuk keluar masuk pit yang jelas serta lokasi muster point.",
                "Rambu – rambu lalu lintas terbuat dari bahan yang memantulkan cahaya (retro reflektif).",
                "Houskeeping/kebersihan areal tambang: bebas dari sampah dan bahan bahan bekas lainnya."
            ]
        },
        {
            title: "Jalan Tambang",
            questions: [
                "Jalan angkut rata (tidak bergelombang) dan bebas dari batuan lepas.",
                "Lebar jalan angkut sesuai dengan standard: (2X lebar kendaraan terbesar untuk jalan satu arah dan atau 3X lebar kendaraan terbesar untuk jalan 2 arah).",
                "Semua jalan angkut dilengkapi dengan tanggul pengaman (bundwall) sesuai dengan tinggi standar ½ X diameter roda kendaraan terbesar.",
                "Tonggak penuntun (post guide) terpasang pada tanggul pengaman dengan interval jarak 30-50 meter.",
                "Drainase atau saluran air berfungsi dengan baik dan tidak terdapat genangan dijalan."
            ]
        },
        {
            title: "Lokasi Parkir LV",
            questions: [
                "Lokasi parkir LV cukup lebar (bisa manuver) dan posisinya datar.",
                "Tidak dilalui/digunakan untuk manuver alat berat (A2B).",
                "Berjarak minimal 30 meter dari alat gali muat yang sedang beroperasi.",
                "Berjarak minimal 1 kali terhadap tinggi tebing."
            ]
        },
        {
            title: "Loading Point",
            questions: [
                "Lebar front loading cukup untuk manuver unit.",
                "Jalur antrian dibuat teratur dan seragam.",
                "Untuk loading dilokasi dekat air atau lumpur dibuat tanggul pengaman."
            ]
        },
        {
            title: "Bench Slope",
            questions: [
                "Dinding galian dalam keadaan stabil, tidak terdapat retakan.",
                "Tidak terdapat material longsor atau material yang menggantung di atas dinding galian yang sewaktu – waktu dapat terjatuh. ",
                "Ketinggian dan kemiringan slope sesuai dengan standar dan bebas dari batuan lepas."
            ]
        },
        {
            title: "Dumping Point",
            questions: [
                "Tempat pembuangan/dumping rata, tidak ada batu besar dan berlumpur.",
                "Dumping area tidak terdapat retakan dan dilengkapi dengan tanggul pengaman yang memadai (½ x tinggi ban terbesar kendaraan yang melakukan dumping)."
            ]
        },
        {
            title: "Base",
            questions: [
                "Berjarak minimal 50 meter dari area loading point.",
                "Berada ditempat yang relatif lebih tinggi dari aktifitas gali muat dan mudah terlihat.",
                "Berjarak minimal 3 meter dari bibir tebing.",
                "Struktur tanah stabil/padat tidak terdapat retakan.",
                "Diagram Obstruksi minimal sudut 15 derajat."
            ]
        }
    ];

    // Helper to calculate flat question number from section and question index
    const getQuestionNumber = (sectionIndex, questionIndex) => {
        let number = 1;
        for (let s = 0; s < sectionIndex; s++) {
            number += questionSections[s].questions.length;
        }
        return number + questionIndex;
    };

    useEffect(() => {
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
            const userData = JSON.parse(storedUser);
            setUserName(userData.nama || "");
            setUserJabatan(userData.jabatan || "");

            // Default date to today
            const today = new Date().toISOString().split('T')[0];
            setFormData(prev => ({ ...prev, tanggal: today }));
        }
    }, []);

    const showModal = (title, message, type = "info", buttons = []) => {
        setModalProps({ title, message, type, buttons });
        setModalVisible(true);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleInspectorChange = (index, field, value) => {
        const newInspectors = [...formData.inspectors];
        newInspectors[index][field] = value;
        setFormData({ ...formData, inspectors: newInspectors });
    };

    const addInspector = () => {
        setFormData({
            ...formData,
            inspectors: [...formData.inspectors, { nama: "", perusahaan: "", jabatan: "" }]
        });
    };

    const removeInspector = (index) => {
        const newInspectors = formData.inspectors.filter((_, i) => i !== index);
        setFormData({ ...formData, inspectors: newInspectors });
    };

    const handleAnswerChange = (sectionIndex, questionIndex, value) => {
        const key = `${sectionIndex}-${questionIndex}`;

        // If changing from No to Yes, clear photo and keterangan for this question
        const newPhotos = { ...formData.photos };
        const newKeterangan = { ...formData.keterangan };

        if (value === "Yes") {
            delete newPhotos[key];
            delete newKeterangan[key];
        }

        setFormData({
            ...formData,
            answers: { ...formData.answers, [key]: value },
            photos: newPhotos,
            keterangan: newKeterangan
        });
    };

    const handlePhotoChange = (sectionIndex, questionIndex, file) => {
        const key = `${sectionIndex}-${questionIndex}`;
        setFormData({
            ...formData,
            photos: { ...formData.photos, [key]: file }
        });
    };

    const handleKeteranganChange = (sectionIndex, questionIndex, value) => {
        const key = `${sectionIndex}-${questionIndex}`;
        setFormData({
            ...formData,
            keterangan: { ...formData.keterangan, [key]: value }
        });
    };

    const removePhoto = (sectionIndex, questionIndex) => {
        const key = `${sectionIndex}-${questionIndex}`;
        const newPhotos = { ...formData.photos };
        delete newPhotos[key];
        setFormData({ ...formData, photos: newPhotos });
    };

    const clearAnswer = (sectionIndex, questionIndex) => {
        const key = `${sectionIndex}-${questionIndex}`;
        const newAnswers = { ...formData.answers };
        delete newAnswers[key];

        const newPhotos = { ...formData.photos };
        delete newPhotos[key];

        const newKeterangan = { ...formData.keterangan };
        delete newKeterangan[key];

        setFormData({
            ...formData,
            answers: newAnswers,
            photos: newPhotos,
            keterangan: newKeterangan
        });
    }
    const validateStep1 = () => {
        if (!formData.tanggal || !formData.lokasi) {
            showModal("Perhatian!", "Mohon isi Tanggal dan Lokasi", "warning");
            return false;
        }
        return true;
    };

    const validateStep2 = () => {
        for (let insp of formData.inspectors) {
            if (!insp.nama || !insp.perusahaan || !insp.jabatan) {
                showModal("Perhatian!", "Mohon lengkapi data semua inpektor", "warning");
                return false;
            }
        }
        return true;
    };

    const validateStep3 = () => {
        // Check if all questions are answered and "No" answers have photo + keterangan
        for (let sIndex = 0; sIndex < questionSections.length; sIndex++) {
            const section = questionSections[sIndex];
            for (let qIndex = 0; qIndex < section.questions.length; qIndex++) {
                const key = `${sIndex}-${qIndex}`;
                const answer = formData.answers[key];

                if (!answer) {
                    showModal("Perhatian!", `Mohon jawab semua pertanyaan checklist`, "warning");
                    return false;
                }

                // If answer is "No", photo and keterangan are required
                if (answer === "No") {
                    if (!formData.photos[key]) {
                        const qNum = getQuestionNumber(sIndex, qIndex);
                        showModal("Perhatian!", `Foto wajib diisi untuk pertanyaan No. ${qNum}`, "warning");
                        return false;
                    }
                    if (!formData.keterangan[key] || formData.keterangan[key].trim() === "") {
                        const qNum = getQuestionNumber(sIndex, qIndex);
                        showModal("Perhatian!", `Keterangan wajib diisi untuk pertanyaan No. ${qNum}`, "warning");
                        return false;
                    }
                }
            }
        }
        return true;
    };

    const nextStep = () => {
        if (step === 1 && !validateStep1()) return;
        if (step === 2 && !validateStep2()) return;
        setStep(step + 1);
    };

    const prevStep = () => setStep(step - 1);

    // Build responses array for backend
    const buildResponses = () => {
        const responses = [];
        let questionNumber = 1;

        for (let sIndex = 0; sIndex < questionSections.length; sIndex++) {
            const section = questionSections[sIndex];
            for (let qIndex = 0; qIndex < section.questions.length; qIndex++) {
                const key = `${sIndex}-${qIndex}`;
                const answer = formData.answers[key] || "";
                responses.push({
                    no_pertanyaan: questionNumber,
                    pertanyaan: section.questions[qIndex],
                    jawaban: answer
                });
                questionNumber++;
            }
        }
        return responses;
    };

    const handleSubmit = async () => {
        if (!validateStep3()) return;

        setIsSubmitting(true);

        try {
            // Build FormData for multipart submission
            const submitData = new FormData();

            // Add basic fields
            submitData.append("tanggal", formData.tanggal);
            submitData.append("lokasi", formData.lokasi);

            // Add inspectors as JSON string
            submitData.append("inspectors", JSON.stringify(formData.inspectors));

            // Build and add responses as JSON string
            const responses = buildResponses();
            submitData.append("responses", JSON.stringify(responses));

            // Add photos and keterangan for "No" answers
            for (let sIndex = 0; sIndex < questionSections.length; sIndex++) {
                const section = questionSections[sIndex];
                for (let qIndex = 0; qIndex < section.questions.length; qIndex++) {
                    const key = `${sIndex}-${qIndex}`;
                    const answer = formData.answers[key];

                    if (answer === "No") {
                        const questionNumber = getQuestionNumber(sIndex, qIndex);

                        // Add photo with field name like "photo_5"
                        if (formData.photos[key]) {
                            submitData.append(`photo_${questionNumber}`, formData.photos[key]);
                        }

                        // Add keterangan with field name like "keterangan_5"
                        if (formData.keterangan[key]) {
                            submitData.append(`keterangan_${questionNumber}`, formData.keterangan[key]);
                        }
                    }
                }
            }

            console.log("Submitting FormData...");

            // Get token from localStorage
            const token = localStorage.getItem("userToken");

            // Call API with multipart/form-data
            const response = await axios.post(
                `${API_URL}/inspection-reports`,
                submitData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        "Authorization": `Bearer ${token}`
                    }
                }
            );

            if (response.data.success) {
                showModal(
                    "Berhasil!",
                    "Laporan Inspeksi telah disimpan",
                    "success",
                    [{
                        text: "OK",
                        onPress: () => navigate("/laporan-safety")
                    }]
                );
            } else {
                showModal("Gagal!", response.data.message || "Gagal menyimpan laporan", "error");
            }
        } catch (error) {
            console.error("Error submitting inspection report:", error);
            const errorMessage = error.response?.data?.message || "Terjadi kesalahan saat menyimpan laporan";
            showModal("Gagal!", errorMessage, "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div>
            {/* Header */}
            <header className="app-header">
                <div className="header-content">
                    <button className="back-button" onClick={() => navigate(-1)}>
                        <Icon>arrow_back</Icon>
                    </button>
                    <h1 className="header-title"> Form Inspeksi</h1>
                    <div className="header-spacer"></div>
                </div>
            </header>
            <div style={{ padding: "20px", fontFamily: "Arial, sans-serif", maxWidth: "800px", margin: "0 auto", paddingBottom: "80px" }}>

                <CustomModal
                    visible={modalVisible}
                    onClose={() => setModalVisible(false)}
                    title={modalProps.title}
                    message={modalProps.message}
                    type={modalProps.type}
                    buttons={modalProps.buttons}
                />

                {/* Progress Bar */}
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "20px", position: "relative" }}>
                    <div style={{ position: "absolute", top: "50%", left: "0", right: "0", height: "4px", backgroundColor: "#e0e0e0", zIndex: 0 }}></div>
                    <div style={{
                        position: "absolute",
                        top: "50%",
                        left: "0",
                        width: step === 1 ? "0%" : step === 2 ? "50%" : "100%",
                        height: "4px",
                        backgroundColor: "#3f8fc4ff",
                        zIndex: 0,
                        transition: "width 0.3s ease"
                    }}></div>

                    {[1, 2, 3].map((s) => (
                        <div key={s} style={{
                            width: "30px",
                            height: "30px",
                            borderRadius: "50%",
                            backgroundColor: step >= s ? "#3f8fc4ff" : "#e0e0e0",
                            color: "white",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: "bold",
                            zIndex: 1,
                            position: "relative"
                        }}>
                            {s}
                        </div>
                    ))}
                </div>

                {/* Step 1: Info Dasar */}
                {step === 1 && (
                    <div className="card" style={styles.card}>
                        <h4>Langkah 1: Informasi Dasar</h4>
                        <div style={styles.inputGroup}>
                            <label>Tanggal</label>
                            <input
                                type="date"
                                name="tanggal"
                                value={formData.tanggal}
                                onChange={handleInputChange}
                                style={styles.input}
                            />
                        </div>
                        <div style={styles.inputGroup}>
                            <label>Lokasi</label>
                            <input
                                type="text"
                                name="lokasi"
                                value={formData.lokasi}
                                onChange={handleInputChange}
                                placeholder="Masukkan Lokasi Inspeksi"
                                style={styles.input}
                            />
                        </div>
                    </div>
                )}

                {/* Step 2: Inspector */}
                {step === 2 && (
                    <div className="card" style={styles.card}>
                        <h4>Langkah 2: Data Inspektor</h4>
                        {formData.inspectors.map((inspector, index) => (
                            <div key={index} style={{ marginBottom: "15px", padding: "10px", border: "1px solid #ddd", borderRadius: "5px" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "5px" }}>
                                    <strong>Inspektor {index + 1}</strong>
                                    {formData.inspectors.length > 1 && (
                                        <button onClick={() => removeInspector(index)} style={{ ...styles.button, backgroundColor: "#ff5252", padding: "5px 10px", fontSize: "12px" }}>Hapus</button>
                                    )}
                                </div>
                                <div style={styles.inputGroup}>
                                    <input type="text" placeholder="Nama" value={inspector.nama} onChange={(e) => handleInspectorChange(index, "nama", e.target.value)} style={styles.input} />
                                </div>
                                <div style={styles.inputGroup}>
                                    <input type="text" placeholder="Perusahaan" value={inspector.perusahaan} onChange={(e) => handleInspectorChange(index, "perusahaan", e.target.value)} style={styles.input} />
                                </div>
                                <div style={styles.inputGroup}>
                                    <input type="text" placeholder="Jabatan" value={inspector.jabatan} onChange={(e) => handleInspectorChange(index, "jabatan", e.target.value)} style={styles.input} />
                                </div>
                            </div>
                        ))}
                        <button onClick={addInspector} style={{ ...styles.button, backgroundColor: "#2196f3", width: "100%" }}>+ Tambah Inspektor</button>
                    </div>
                )}

                {/* Step 3: Checklist with Sections */}
                {step === 3 && (
                    <div>
                        <div style={{ marginBottom: "10px", fontWeight: "bold" }}>Langkah 3: Checklist Inspeksi</div>
                        {questionSections.map((section, sIndex) => (
                            <div key={sIndex} className="card" style={{ ...styles.card, borderTop: "4px solid #2196f3" }}>
                                <h4 style={{ marginTop: 0, color: "#1976d2", borderBottom: "1px solid #eee", paddingBottom: "10px", fontWeight: "bold" }}>{section.title}</h4>
                                {section.questions.map((q, qIndex) => {
                                    const key = `${sIndex}-${qIndex}`;
                                    const isNo = formData.answers[key] === "No";
                                    const questionNumber = getQuestionNumber(sIndex, qIndex);

                                    return (
                                        <div key={qIndex} style={{ marginBottom: "15px", borderBottom: "1px solid #f5f5f5", paddingBottom: "15px" }}>
                                            <p style={{ margin: "0 0 10px 0", fontSize: "14px" }}>{questionNumber}. {q}</p>
                                            <div style={{ display: "flex", gap: "20px", marginBottom: isNo ? "15px" : "0", alignItems: "center" }}>
                                                <label style={{ cursor: "pointer", display: "flex", alignItems: "center" }}>
                                                    <input
                                                        type="radio"
                                                        name={`q-${sIndex}-${qIndex}`}
                                                        value="Yes"
                                                        checked={formData.answers[key] === "Yes"}
                                                        onChange={() => handleAnswerChange(sIndex, qIndex, "Yes")}
                                                        style={{ marginRight: "5px" }}
                                                    />
                                                    <span style={{ color: "#4caf50", fontWeight: "bold" }}>Yes</span>
                                                </label>
                                                <label style={{ cursor: "pointer", display: "flex", alignItems: "center" }}>
                                                    <input
                                                        type="radio"
                                                        name={`q-${sIndex}-${qIndex}`}
                                                        value="No"
                                                        checked={formData.answers[key] === "No"}
                                                        onChange={() => handleAnswerChange(sIndex, qIndex, "No")}
                                                        style={{ marginRight: "5px" }}
                                                    />
                                                    <span style={{ color: "#f44336", fontWeight: "bold" }}>No</span>
                                                </label>
                                                {formData.answers[key] && (
                                                    <button
                                                        onClick={() => clearAnswer(sIndex, qIndex)}
                                                        style={{
                                                            background: "none",
                                                            border: "none",
                                                            cursor: "pointer",
                                                            color: "#9e9e9e",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            padding: "5px"
                                                        }}
                                                        title="Hapus Pilihan"
                                                    >
                                                        <Icon style={{ fontSize: "20px" }}>highlight_off</Icon>
                                                    </button>
                                                )}
                                            </div>

                                            {/* Show photo and keterangan inputs when "No" is selected */}
                                            {isNo && (
                                                <div style={{
                                                    backgroundColor: "#fff3e0",
                                                    padding: "15px",
                                                    borderRadius: "8px",
                                                    border: "1px solid #ffcc80",
                                                    marginTop: "10px"
                                                }}>
                                                    <p style={{ margin: "0 0 10px 0", fontSize: "13px", color: "#e65100", fontWeight: "bold" }}>
                                                        <Icon style={{ fontSize: "16px", verticalAlign: "middle", marginRight: "5px" }}>warning</Icon>
                                                        Foto dan Keterangan wajib diisi
                                                    </p>

                                                    {/* Photo Upload */}
                                                    <div style={{ marginBottom: "10px" }}>
                                                        <label style={{ fontSize: "13px", fontWeight: "bold", display: "block", marginBottom: "5px" }}>
                                                            Foto Bukti <span style={{ color: "red" }}>*</span>
                                                        </label>
                                                        {formData.photos[key] ? (
                                                            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                                                <img
                                                                    src={URL.createObjectURL(formData.photos[key])}
                                                                    alt="Preview"
                                                                    style={{ width: "80px", height: "80px", objectFit: "cover", borderRadius: "5px", border: "1px solid #ddd" }}
                                                                />
                                                                <div>
                                                                    <p style={{ margin: 0, fontSize: "12px", color: "#666" }}>{formData.photos[key].name}</p>
                                                                    <button
                                                                        onClick={() => removePhoto(sIndex, qIndex)}
                                                                        style={{ ...styles.button, backgroundColor: "#ff5252", padding: "5px 10px", fontSize: "11px", marginTop: "5px" }}
                                                                    >
                                                                        Hapus Foto
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <label style={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                padding: "15px",
                                                                border: "2px dashed #ffcc80",
                                                                borderRadius: "8px",
                                                                cursor: "pointer",
                                                                backgroundColor: "#fff8e1"
                                                            }}>
                                                                <input
                                                                    type="file"
                                                                    accept="image/*"
                                                                    style={{ display: "none" }}
                                                                    onChange={(e) => {
                                                                        if (e.target.files[0]) {
                                                                            handlePhotoChange(sIndex, qIndex, e.target.files[0]);
                                                                        }
                                                                    }}
                                                                />
                                                                <Icon style={{ marginRight: "8px", color: "#ff9800" }}>camera_alt</Icon>
                                                                <span style={{ color: "#ff9800", fontWeight: "bold" }}>Pilih Foto</span>
                                                            </label>
                                                        )}
                                                    </div>

                                                    {/* Keterangan Input */}
                                                    <div>
                                                        <label style={{ fontSize: "13px", fontWeight: "bold", display: "block", marginBottom: "5px" }}>
                                                            Keterangan <span style={{ color: "red" }}>*</span>
                                                        </label>
                                                        <textarea
                                                            placeholder="Masukkan keterangan..."
                                                            value={formData.keterangan[key] || ""}
                                                            onChange={(e) => handleKeteranganChange(sIndex, qIndex, e.target.value)}
                                                            style={{
                                                                ...styles.input,
                                                                minHeight: "60px",
                                                                resize: "vertical",
                                                                fontFamily: "inherit"
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                )}

                {/* Footer Buttons */}
                <div style={{ marginTop: "20px", display: "flex", justifyContent: "space-between" }}>
                    {step > 1 ? (
                        <button onClick={prevStep} style={{ ...styles.button, backgroundColor: "#757575" }} disabled={isSubmitting}>Kembali</button>
                    ) : (
                        <button onClick={() => navigate("/laporan-safety")} style={{ ...styles.button, backgroundColor: "#757575" }}>Batal</button>
                    )}

                    {step < 3 ? (
                        <button onClick={nextStep} style={{ ...styles.button, backgroundColor: "#4caf50" }}>Lanjut</button>
                    ) : (
                        <button
                            onClick={handleSubmit}
                            style={{ ...styles.button, backgroundColor: isSubmitting ? "#9e9e9e" : "#2196f3" }}
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? "Menyimpan..." : "Submit"}
                        </button>
                    )}
                </div>

            </div>
        </div>
    );
}

const styles = {
    card: {
        backgroundColor: "#f3f2f2ff",
        padding: "20px",
        borderRadius: "10px",
        boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
        marginBottom: "20px"
    },
    inputGroup: {
        marginBottom: "15px"
    },
    input: {
        width: "100%",
        padding: "10px",
        borderRadius: "5px",
        border: "1px solid #ddd",
        boxSizing: "border-box"
    },
    button: {
        padding: "10px 20px",
        color: "white",
        border: "none",
        borderRadius: "5px",
        cursor: "pointer",
        fontSize: "14px"
    }
};
